import { $, $$ } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { toastError, toastSuccess } from '../lib/toast.js';

/**
 * Module activation and gallery search.
 *
 * Mounted on every dashboard page: the activation switch also exists in the
 * header of the module pages, and the search lives in the top bar. Each block
 * returns immediately when its anchor point is absent.
 */
export function initGalleryPage(): void {
  mountToggles();
  mountSearch();
}

/** Activation switches, in the gallery as well as in the module header. */
function mountToggles(): void {
  for (const toggle of $$<HTMLInputElement>('[data-module-toggle]')) {
    toggle.addEventListener('change', () => {
      const key = toggle.dataset.moduleToggle ?? '';
      const guildId = toggle.dataset.guildId ?? '';
      const enabled = toggle.checked;

      void (async () => {
        toggle.disabled = true;
        try {
          await api.patch(`/api/modules?guildId=${guildId}`, {
            guildId,
            key,
            enabled,
          });
          toastSuccess(
            enabled
              ? ct('moduleEnabled', 'Module enabled.')
              : ct('moduleDisabled', 'Module disabled.'),
          );
        } catch (error) {
          // Restore the visual state: leaving the switch in a position the
          // server refused would suggest a configuration that does not exist.
          toggle.checked = !enabled;
          toastError(error);
        } finally {
          toggle.disabled = false;
        }
      })();
    });
  }
}

/**
 * Gallery filtering, and the search that is not on the gallery.
 *
 * The field lives in the dashboard's top bar, on every page. On the gallery it
 * filters the cards as you type — they are already in the DOM, and one request
 * per keystroke would be pure waste. Everywhere else it used to do **nothing**
 * until you found the Enter key, and then dropped what you had typed: a search
 * box that answers only one page in forty is a search box that reads as broken.
 *
 * So off the gallery it takes you there, carrying the words with it.
 */
function mountSearch(): void {
  const input = $<HTMLInputElement>('[data-module-search]');
  const gallery = $('[data-gallery]');
  if (!input) return;

  const guildId = new URLSearchParams(window.location.search).get('guildId') ?? '';

  if (!gallery) {
    leaveForGallery(input, guildId);
    return;
  }

  const empty = $('[data-gallery-empty]');
  const cards = $$<HTMLElement>('[data-module-card]');
  const groups = $$<HTMLElement>('[data-group]');

  const filter = () => {
    const query = input.value.trim().toLowerCase();
    let visible = 0;

    for (const card of cards) {
      const match = query === '' || (card.dataset.search ?? '').includes(query);
      card.classList.toggle('hidden', !match);
      if (match) visible += 1;
    }

    // A section title with no visible card no longer has any reason to show.
    for (const group of groups) {
      const hasVisible = [...group.querySelectorAll('[data-module-card]')].some(
        (card) => !card.classList.contains('hidden'),
      );
      group.classList.toggle('hidden', !hasVisible);
    }

    empty?.classList.toggle('hidden', visible > 0);
  };

  input.addEventListener('input', filter);

  /*
   * The words typed on the page before this one.
   *
   * They arrive in the address, so the gallery opens already filtered and the
   * caret is put back at the end of them: whoever was typing carries on typing,
   * which is the whole point of having been sent here.
   */
  const carried = new URLSearchParams(window.location.search).get('q') ?? '';

  if (carried) {
    input.value = carried;
    filter();
    input.focus();
    input.setSelectionRange(carried.length, carried.length);
  }
}

/** How long a page waits before deciding somebody is searching, not mistyping. */
const LEAVE_AFTER_MS = 450;

/**
 * Off the gallery: typing opens the gallery with what was typed.
 *
 * After a pause rather than on the first letter — a page that navigates on
 * keystroke one takes the field away mid-word — and never on a single
 * character, which is nearly always the start of something rather than a
 * search. Enter goes immediately, for anyone who does not want to wait.
 */
function leaveForGallery(input: HTMLInputElement, guildId: string): void {
  let timer = 0;

  const go = () => {
    const query = input.value.trim();
    if (query.length < 2) return;

    window.clearTimeout(timer);
    window.location.href = `/modules?guildId=${guildId}&q=${encodeURIComponent(query)}`;
  };

  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(go, LEAVE_AFTER_MS);
  });

  input.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    go();
  });
}
